import { Component, forwardRef, signal, input } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { COMMA, ENTER } from '@angular/cdk/keycodes';

@Component({
    selector: 'app-input-multi',
    templateUrl: './input-multi.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => InputMultiComponent),
            multi: true
        }
    ],
    standalone: false
})
export class InputMultiComponent implements ControlValueAccessor {
    public readonly formControl = input.required<FormControl<string[]>>();

    public readonly label = input<string>('');

    public readonly placeholder = input<string>('');

    public readonly hint = input<string>('');

    public readonly addOnBlur = input<boolean>(true);

    public readonly separatorKeysCodes = input<readonly number[]>([ENTER, COMMA] as const);

    protected readonly items = signal<string[]>([]);

    // eslint-disable-next-line @typescript-eslint/no-empty-function, @typescript-eslint/no-unused-vars
    private onChange = (value: string[]): void => { };

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    private onTouched = (): void => { };

    private isDisabled = false;

    public writeValue(value: string[] | null): void {
        this.items.set(value || []);
    }

    public registerOnChange(fn: (value: string[]) => void): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    protected get disabled(): boolean {
        return this.isDisabled;
    }

    protected handleBlur(): void {
        this.onTouched();
    }

    protected addItem(event: MatChipInputEvent): void {
        const value = (event.value || '').trim();

        if (value) {
            this.items.update(items => {
                const newItems = [...items, value];
                this.onChange(newItems);
                this.onTouched();

                return newItems;
            });
        }

        if (event.chipInput) {
            event.chipInput.clear();
        }
    }

    protected removeItem(item: string): void {
        this.items.update(items => {
            const index = items.indexOf(item);

            if (index < 0) {
                return items;
            }

            this.announcer.announce(`Removed ${item}`);

            const newItems = [...items];

            newItems.splice(index, 1);
            this.onChange(newItems);
            this.onTouched();

            return newItems;
        });
    }

    constructor(
        private announcer: LiveAnnouncer
    ) {
    }
}
